import { resolveCountryBucket } from "@screencloud/billing-client-api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Constants } from "src/billinglatest/constants";
import {
  emptyPixiAddress,
  type PixiAddress,
  type PixiPaymentMethod,
  type ShippingService,
  type StepProps,
} from "../../../hooks/usePixiWizardState";
import {
  isBillingUnsupported,
  resolveEffectiveShipping,
} from "../../../effectiveShipping";
import { DEFAULT_HARDWARE_CHECKOUT_COUNTRY } from "../../../shippingCountries";
import { isShippingSupported } from "../utils/supportedShippingCountries";
import { availableShippingServices } from "../../CheckoutStep/shippingRates";
import { useShippingRates } from "../../CheckoutStep/useShippingRates";
import { DEFAULT_CURRENCY } from "../../PlanBillingStep/constants";
import { resolveEffectiveDealId } from "../../PlanBillingStep/selection";
import {
  type AddressErrorReason,
  type AddressErrors,
  type AddressField,
  hasErrors,
  validateAddress,
} from "../utils/validation";

export type AddressFormKind = "billing" | "shipping";

/**
 * Shipping services the wizard can render (it has a label for each). The rate
 * table only ever carries these today; any unknown service the backend might add
 * is filtered out until the UI models it, rather than shown unlabelled.
 */
const KNOWN_SHIPPING_SERVICES: ShippingService[] = ["standard", "express"];

const touchKey = (form: AddressFormKind, field: AddressField): string =>
  `${form}.${field}`;

export interface UsePaymentShippingForm {
  paymentMethod: PixiPaymentMethod;
  billing: PixiAddress;
  /** Shipping values to display (contact may be seeded from billing). */
  shipping: PixiAddress;
  useSameAddress: boolean;
  showShippingForm: boolean;
  /**
   * Whether the shipping address fields are read-only. True when "same address"
   * is ticked and the billing country is shippable: the card stays visible with
   * billing values mirrored in, but only the shipping-method dropdown is
   * editable. The unsupported-billing case keeps the fields editable.
   */
  shippingDisabled: boolean;
  showUnsupportedWarning: boolean;
  /** Whether the same-address toggle is shown (hidden when no hardware ships). */
  showSameAddressToggle: boolean;
  /**
   * Hardware shipping services the destination actually offers, read from the
   * backend rate table (standard, express, or both) — never a hardcoded pair.
   * Empty while the rates load or when the destination has no rate.
   */
  shippingServiceOptions: ShippingService[];
  /**
   * The selected shipping service, always constrained to one the destination
   * offers ({@link shippingServiceOptions}). undefined only when no service is
   * available yet (rates loading / unsupported destination).
   */
  shippingService?: ShippingService;
  unsupportedCountryName: string;
  /** Billing-only PO number (optional, invoice reference). */
  poNumber: string;
  /** Billing-only VAT number (validated by Chargebee at checkout, not here). */
  vatNumber: string;
  /**
   * Whether to show the VAT number input. True only for VAT-eligible billing
   * countries (EU + GB), matching the billing model (`Constants.VatCountries`).
   */
  showVatInput: boolean;
  /**
   * Whether to show the "sales tax will be added" banner instead of a VAT input.
   * True for US/CA billing (`Constants.TaxableCountries`).
   */
  showTaxBanner: boolean;
  /** Billing country code (ISO alpha-2), used as the VAT input label. */
  billingCountry: string;
  isValid: boolean;
  setPaymentMethod: (method: PixiPaymentMethod) => void;
  setPoNumber: (value: string) => void;
  setVatNumber: (value: string) => void;
  setShippingService: (service: ShippingService) => void;
  updateBilling: (field: AddressField, value: string) => void;
  updateShipping: (field: AddressField, value: string) => void;
  setUseSameAddress: (checked: boolean) => void;
  setCardPaymentMethodId: (id: string | null) => void;
  markTouched: (form: AddressFormKind, field: AddressField) => void;
  errorReason: (
    form: AddressFormKind,
    field: AddressField,
  ) => AddressErrorReason | undefined;
}

/**
 * Owns the Payment & Shipping step's form state and derivations: payment
 * method selection, billing/shipping addresses, the same-address toggle, the
 * unsupported-shipping-country case, field validation and the overall validity
 * reported up to the wizard footer.
 *
 * Billing and shipping are stored separately so manual shipping entries are
 * preserved across same-address toggles. While same-address is checked the
 * effective shipping address is billing (computed, not written over the manual
 * entry), unless billing is in an unsupported shipping country — in which case
 * the shipping form is shown with contact info seeded from billing but address
 * fields left blank.
 */
export const usePaymentShippingForm = ({
  state,
  updateState,
  countryName,
}: StepProps & {
  countryName: (countryCode: string) => string;
}): UsePaymentShippingForm => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const { rates } = useShippingRates();

  const paymentMethod: PixiPaymentMethod = state.paymentMethod ?? "credit_card";
  const useSameAddress = state.useSameAddressForShipping ?? false;
  // Billing-only fields (GRWBKK-1704). Neither gates step-3 Continue: PO is
  // optional and VAT is validated by Chargebee at checkout, so they stay out of
  // `isValid` below.
  const poNumber = state.poNumber ?? "";
  const vatNumber = state.vatNumber ?? "";

  // The services the destination actually offers, read from the backend rates.
  // Mirror the exact inputs the Checkout pricing uses (deal "Ship to" country,
  // the locked currency and the screen count → quantity tier) so the picker and
  // the charged rate can never disagree. A standard-only country shows only
  // Standard; both-service countries show both — nothing is hardcoded.
  const shippingServiceOptions = useMemo<ShippingService[]>(
    () =>
      availableShippingServices(rates, {
        countryBucket:
          resolveCountryBucket(
            state.shippingCountry ?? DEFAULT_HARDWARE_CHECKOUT_COUNTRY,
          ) ?? undefined,
        currency: state.currency ?? DEFAULT_CURRENCY,
        screenCount: state.screenCount,
      }).filter((service): service is ShippingService =>
        KNOWN_SHIPPING_SERVICES.includes(service as ShippingService),
      ),
    [rates, state.shippingCountry, state.currency, state.screenCount],
  );

  // Keep the selected service within what's offered: honour the stored pick when
  // it's still available, otherwise fall back to the first offered service. Never
  // assume "standard" — when nothing is offered there is simply no selection.
  const shippingService: ShippingService | undefined =
    state.shippingService &&
    shippingServiceOptions.includes(state.shippingService)
      ? state.shippingService
      : shippingServiceOptions[0];

  const billing = useMemo(
    () => state.billingAddress ?? emptyPixiAddress(),
    [state.billingAddress],
  );
  // Preselect the shipping country from the deal step's "Ship to" choice
  // (GRWBKK-1667). The deal card always displays a resolved country — the
  // user's pick or the default — so mirror that here for the two screens to
  // agree. Seed only while the form country is still blank; once the user sets
  // a country (or edits any shipping field) the stored value wins, keeping it
  // editable and preserving manual entries. Only reached when "same address" is
  // unticked — while ticked the effective shipping is billing.
  const dealShippingCountry =
    state.shippingCountry ?? DEFAULT_HARDWARE_CHECKOUT_COUNTRY;
  const manualShipping = useMemo(() => {
    const stored = state.shippingAddress ?? emptyPixiAddress();
    if (stored.country.trim()) return stored;
    return { ...stored, country: dealShippingCountry };
  }, [state.shippingAddress, dealShippingCountry]);

  const billingCountry = billing.country.trim();
  const billingUnsupported = isBillingUnsupported(billing);

  // VAT display is country-dependent, mirroring the billing model
  // (`src/billinglatest/forms/Vat`): VAT-eligible countries (EU + GB) get the
  // VAT input labelled with their country code; US/CA get the sales-tax banner
  // instead; everything else shows neither.
  const showVatInput = Constants.VatCountries.includes(billingCountry);
  const showTaxBanner = Constants.TaxableCountries.includes(billingCountry);

  // The "discount" deal ships no hardware, so there is no shipping at all:
  // hide the shipping form, the same-address toggle and the warning entirely.
  // Derive from the valid deal so a stale free-device left over for an `Other`
  // shipping country (which resolves to discount) shows no shipping (GRWBKK-1730).
  const needsShipping = resolveEffectiveDealId(state) !== "discount";

  // The shipping card is shown whenever hardware ships. When "same address" is
  // ticked and the billing country is shippable, the fields mirror billing and
  // render read-only (the shipping-method dropdown stays editable). When billing
  // is in an unsupported country the fields stay editable so the user can change
  // the mirrored (unsupported) address to a supported one.
  const showShippingForm = needsShipping;
  const shippingDisabled =
    needsShipping && useSameAddress && !billingUnsupported;

  /**
   * Shipping values to display. While "same address" is ticked the effective
   * shipping is billing (mirrored fully, including the country) — read-only for
   * a shippable billing country, editable + warned for an unsupported one. Once
   * unticked (manually or by editing the form) the manual shipping entry is used.
   */
  const shipping = useMemo<PixiAddress>(
    () =>
      resolveEffectiveShipping({
        needsShipping,
        useSameAddress,
        billing,
        manualShipping,
      }),
    [needsShipping, useSameAddress, billing, manualShipping],
  );

  // The unsupported-country warning follows the *selected shipping* country
  // whenever hardware ships to a set-but-unsupported destination. Same-address
  // over an unsupported billing country mirrors that country in (so it shows
  // first), and it keeps tracking the user's choice after they edit — which
  // unticks "same address" — naming whatever they pick and clearing the moment
  // that pick is shippable. The read-only shippable-mirror case never trips it
  // (shipping mirrors a supported billing country).
  const showUnsupportedWarning =
    needsShipping &&
    !!shipping.country.trim() &&
    !isShippingSupported(shipping.country);

  const billingErrors: AddressErrors = useMemo(
    () =>
      validateAddress(billing, {
        requireState: true,
        requireShippingSupported: false,
      }),
    [billing],
  );

  const shippingErrors: AddressErrors = useMemo(
    () =>
      validateAddress(shipping, {
        requireState: true,
        requireShippingSupported: true,
      }),
    [shipping],
  );

  // Credit card needs a tokenized paymentMethod.id (held by Stripe). Every other
  // method (Invoice, PayPal, Direct Debit) has no step-3 input to validate — its
  // gateway operation happens at step 4 (Checkout).
  const cardTokenized = !!state.cardPaymentMethodId;
  const paymentValid = paymentMethod !== "credit_card" || cardTokenized;
  const billingValid = !hasErrors(billingErrors);
  // No hardware shipping, or shipping mirrors a valid billing address → nothing
  // extra to validate; otherwise the editable shipping fields must be valid.
  const shippingValid =
    !needsShipping || shippingDisabled || !hasErrors(shippingErrors);
  const isValid = paymentValid && billingValid && shippingValid;

  // Report validity up so the wizard footer can gate Continue.
  useEffect(() => {
    if (state.paymentShippingValid !== isValid) {
      updateState({ paymentShippingValid: isValid });
    }
  }, [isValid, state.paymentShippingValid, updateState]);

  // Keep the shipping country in step with the address the user is actually
  // shipping to. The shipping-method options and the Checkout pricing both key
  // off `state.shippingCountry`, so when the shipping-address country changes (or
  // mirrors a different billing country via "same address") the offered services
  // and the charged rate follow it. Only propagates a real, *shippable* country:
  // the unsupported same-address case mirrors the billing country into the form
  // (so it reads as the same address), but that unsupported code must not leak
  // into the deal "Ship to" or the rate lookup — the user picks a supported one.
  useEffect(() => {
    const effectiveCountry = shipping.country.trim();
    if (
      effectiveCountry &&
      isShippingSupported(effectiveCountry) &&
      effectiveCountry !== state.shippingCountry
    ) {
      updateState({ shippingCountry: effectiveCountry });
    }
  }, [shipping.country, state.shippingCountry, updateState]);

  // Clear a stale VAT number when the billing country no longer shows the VAT
  // input (switched to US/CA or a non-VAT country). Without this a VAT entered
  // for e.g. GB would still be submitted to Chargebee against the new country at
  // checkout and fail-closed abort the purchase. Writes only when there is
  // something to clear, so it settles after one pass.
  useEffect(() => {
    if (!showVatInput && vatNumber) {
      updateState({ vatNumber: "" });
    }
  }, [showVatInput, vatNumber, updateState]);

  // Persist the displayed default method so later steps never have to guess it
  // (Checkout must not assume a default). Writes only when unset.
  useEffect(() => {
    if (state.paymentMethod === undefined) {
      updateState({ paymentMethod: "credit_card" });
    }
  }, [state.paymentMethod, updateState]);

  // Switching method drops any card token and resets validity in the same
  // update: the credit-card fields unmount (or mount empty when switching back),
  // so a token from a prior card entry is stale, and a `true` carried over from
  // a no-card method (PayPal/Invoice) must not keep Continue enabled over an
  // empty card form. Recomputed immediately from the newly selected method.
  const setPaymentMethod = useCallback(
    (method: PixiPaymentMethod) =>
      updateState({
        paymentMethod: method,
        cardPaymentMethodId: undefined,
        paymentShippingValid: false,
      }),
    [updateState],
  );

  const setPoNumber = useCallback(
    (value: string) => updateState({ poNumber: value }),
    [updateState],
  );

  const setVatNumber = useCallback(
    (value: string) => updateState({ vatNumber: value }),
    [updateState],
  );

  const setShippingService = useCallback(
    (service: ShippingService) => updateState({ shippingService: service }),
    [updateState],
  );

  const updateBilling = useCallback(
    (field: AddressField, value: string) =>
      updateState({ billingAddress: { ...billing, [field]: value } }),
    [billing, updateState],
  );

  const updateShipping = useCallback(
    (field: AddressField, value: string) =>
      updateState({
        shippingAddress: { ...shipping, [field]: value },
        // Editing shipping is only possible in the unsupported same-address case
        // (the shippable mirror is read-only). Touching it means the shipping
        // address is no longer "the same as billing", so untick the toggle and
        // let the two addresses live independently from here on.
        ...(useSameAddress ? { useSameAddressForShipping: false } : {}),
      }),
    [shipping, useSameAddress, updateState],
  );

  const setUseSameAddress = useCallback(
    (checked: boolean) => updateState({ useSameAddressForShipping: checked }),
    [updateState],
  );

  const setCardPaymentMethodId = useCallback(
    (id: string | null) =>
      updateState({ cardPaymentMethodId: id ?? undefined }),
    [updateState],
  );

  const markTouched = useCallback(
    (form: AddressFormKind, field: AddressField) =>
      setTouched((prev) => ({ ...prev, [touchKey(form, field)]: true })),
    [],
  );

  const errorReason = useCallback(
    (
      form: AddressFormKind,
      field: AddressField,
    ): AddressErrorReason | undefined => {
      // Read-only shipping fields mirror validated billing; no errors shown.
      if (form === "shipping" && shippingDisabled) return undefined;
      // Unsupported shipping country is called out by the yellow warning, so
      // don't also flag the (filled) country field inline. A blank country
      // still shows its "required" error (the warning is off then).
      if (
        form === "shipping" &&
        field === "country" &&
        showUnsupportedWarning
      ) {
        return undefined;
      }
      const errors: AddressErrors =
        form === "billing" ? billingErrors : shippingErrors;
      const reason = errors[field];
      if (!reason) return undefined;
      // Bad email format surfaces immediately, as in the design; every other
      // error waits until the field is touched (blurred).
      if (reason === "email_invalid") return reason;
      return touched[touchKey(form, field)] ? reason : undefined;
    },
    [
      billingErrors,
      shippingErrors,
      touched,
      shippingDisabled,
      showUnsupportedWarning,
    ],
  );

  return {
    paymentMethod,
    billing,
    shipping,
    useSameAddress,
    showShippingForm,
    shippingDisabled,
    showUnsupportedWarning,
    showSameAddressToggle: needsShipping,
    shippingServiceOptions,
    shippingService,
    unsupportedCountryName: showUnsupportedWarning
      ? countryName(shipping.country)
      : "",
    poNumber,
    vatNumber,
    showVatInput,
    showTaxBanner,
    billingCountry,
    isValid,
    setPaymentMethod,
    setPoNumber,
    setVatNumber,
    setShippingService,
    updateBilling,
    updateShipping,
    setUseSameAddress,
    setCardPaymentMethodId,
    markTouched,
    errorReason,
  };
};
